import { Checkbox, Text } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import {
  BooleanQuestion,
  FormEquipmentTypesField,
  FormMonthYearField,
  FormTextField,
} from 'src/components/Overlays/EmploymentDetailsDrawer/components'

import type { EmploymentForm } from './formValues'
import { SectionTitle, StageActions } from './stageParts'

export interface JobStageProps {
  form: EmploymentForm
  saving: boolean
  onSave: () => void
  /** The section is folded to its title line. */
  folded?: boolean
  onToggleFold?: () => void
}

const asDate = (value: Date | string | number | null | undefined) =>
  value ? (value instanceof Date ? value : new Date(value)) : undefined

type BooleanField =
  | 'isDriverTerminated'
  | 'isDriverSubjectToFMCSRs'
  | 'isJobDesignatedAsSafetySensitiveFunctionUnderDOT'
  | 'shouldContactEmployer'

const JobStage = ({
  form,
  saving,
  onSave,
  folded = false,
  onToggleFold,
}: JobStageProps) => {
  const booleanProps = (field: BooleanField) => ({
    value: form.values[field]?.toString(),
    onChange: (value: string) => form.setFieldValue(field, value === 'true'),
  })

  if (folded) {
    return (
      <div className="space-y-3">
        <SectionTitle folded onToggle={onToggleFold}>
          Job information
        </SectionTitle>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <SectionTitle folded={false} onToggle={onToggleFold}>
        Job information
      </SectionTitle>
      <FormTextField
        label="Position"
        required
        editing
        formProps={{
          ...form.getInputProps('position'),
          placeholder: 'Enter position',
        }}
      />
      <FormMonthYearField
        label="Start Date"
        required
        editing
        formProps={{
          ...form.getInputProps('startDate'),
          maxDate: asDate(form.values.endDate) ?? new Date(),
        }}
      />
      <FormMonthYearField
        label="End Date"
        editing
        formProps={{
          ...form.getInputProps('endDate'),
          minDate: asDate(form.values.startDate),
          maxDate: new Date(),
          disabled: !form.values.endDate,
        }}
      />
      <div className="flex flex-row items-center justify-between">
        <Text size="xs" fw={300}>
          Current employer
        </Text>
        <div className="flex w-[250px] justify-end">
          <Checkbox
            size="xs"
            aria-label="Current employer"
            onChange={(event) => {
              const nowCurrent = event.currentTarget.checked
              form.setFieldValue('endDate', nowCurrent ? null : new Date())
              form.setFieldValue('shouldContactEmployer', !nowCurrent)
            }}
            checked={!form.values.endDate}
          />
        </div>
      </div>
      <FormTextField
        label="Reason for leaving"
        editing
        required={!!form.values.endDate}
        formProps={{
          ...form.getInputProps('reasonForLeaving'),
          placeholder: 'Enter reason for leaving',
        }}
      />
      <FormEquipmentTypesField
        label="Equipment Types"
        editing
        formProps={{ ...form.getInputProps('equipmentTypes') }}
      />
      <BooleanQuestion
        label="Was this driver terminated?"
        editing
        formProps={booleanProps('isDriverTerminated')}
      />
      <BooleanQuestion
        label="Was the driver subject to FMCSRs while employed?"
        editing
        formProps={booleanProps('isDriverSubjectToFMCSRs')}
      />
      <BooleanQuestion
        label="Was the job designated as a safety-sensitive function under DOT?"
        editing
        formProps={booleanProps(
          'isJobDesignatedAsSafetySensitiveFunctionUnderDOT'
        )}
      />
      <BooleanQuestion
        label="Can we contact the employer?"
        editing
        formProps={booleanProps('shouldContactEmployer')}
      />
      <StageActions>
        <DNButton variant="primary" onClick={onSave} disabled={saving}>
          Save
        </DNButton>
      </StageActions>
    </div>
  )
}

export default JobStage
